import {
  MmmModelOutput,
  MarketingSourceCategoryForecast
} from '../mmmModelOutput';
import {
  ChartByStringBarScatter,
  ChartDataType,
  ChartDisplayType,
  DataPointSetByString,
  DataPointSetByStringYAxis
} from 'features/charts/chart.models';
import { v4 as guid } from 'uuid';

export const createmodelledRoiteByChannelChart = (
  ddaData: MmmModelOutput
): ChartByStringBarScatter => {
  const dataPointStrings = ddaData.forecasts.map(
    (forecast) => forecast.sourceCategoryDisplayName
  );

  const spendDataSets = createSpendDataSets(
    ddaData.forecasts,
    dataPointStrings
  );

  const revenueDataSets = createRevenueDataSets(
    ddaData.forecasts,
    dataPointStrings
  );

  const barDataPointSets = [...spendDataSets, ...revenueDataSets];
  const yHighestValue = barDataPointSets.reduce(
    (highestValue, currentValue) =>
      currentValue.value > highestValue ? currentValue.value : highestValue,
    0
  );

  const scatterDataPointSets = createRoiDataSets(
    ddaData.forecasts,
    dataPointStrings
  );
  const yScatterHighestValue = scatterDataPointSets.reduce(
    (highestValue, currentValue) =>
      currentValue.value > highestValue ? currentValue.value : highestValue,
    0
  );

  return {
    id: guid(),
    type: ChartDataType.STRING_BAR_SCATTER,
    defaultChartDisplayType: ChartDisplayType.MIXED,
    shouldShowChartTypeDropdown: false,
    title: '',
    barDataPointSets,
    scatterDataPointSets,
    yAxisFormat: new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      maximumSignificantDigits: 1,
      notation: 'compact'
    }),
    tooltipFormat: new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      maximumSignificantDigits: 3,
      notation: 'compact'
    }),
    xAxisTitle: '',
    yAxisTitle: 'Spend',
    yHighestValue,
    yScatterHighestValue
  };

  function createSpendDataSets(
    forecasts: MarketingSourceCategoryForecast[],
    dataPointStrings: string[]
  ): DataPointSetByString[] {
    const dataSets = dataPointStrings.map((dataPointString) => {
      const spend =
        forecasts.find(
          (forecast) => forecast.sourceCategoryDisplayName === dataPointString
        )?.spend ?? 0;

      const dataPointSet: DataPointSetByString = {
        dataPointString,
        dataPointSetName: 'Spend',
        value: spend * ddaData.dayMultiplier
      };

      return dataPointSet;
    });

    return dataSets;
  }

  function createRevenueDataSets(
    forecasts: MarketingSourceCategoryForecast[],
    dataPointStrings: string[]
  ): DataPointSetByString[] {
    const dataSets = dataPointStrings.map((dataPointString) => {
      const revenue =
        forecasts.find(
          (forecast) => forecast.sourceCategoryDisplayName === dataPointString
        )?.revenue ?? 0;

      const dataPointSet: DataPointSetByString = {
        dataPointString,
        dataPointSetName: 'Revenue',
        value: revenue * ddaData.dayMultiplier
      };

      return dataPointSet;
    });

    return dataSets;
  }

  function createRoiDataSets(
    forecasts: MarketingSourceCategoryForecast[],
    dataPointStrings: string[]
  ): DataPointSetByStringYAxis[] {
    const roi = dataPointStrings.map((dataPointString) => {
      const forecast = forecasts.find(
        (forecast) => forecast.sourceCategoryDisplayName === dataPointString
      );

      if (!forecast) {
        throw Error(`Unable to find forecast with name ${dataPointString}`);
      }

      const set: DataPointSetByStringYAxis = {
        dataPointSetName: 'ROI',
        dataPointString,
        yAxisName: 'y1',
        yAxisFormat: new Intl.NumberFormat('en-GB', {
          style: 'percent',
          maximumFractionDigits: 1
        }),
        yAxisTooltipFormat: new Intl.NumberFormat('en-GB', {
          style: 'percent',
          maximumFractionDigits: 2
        }),
        yAxisTitle: '',
        value: forecast.revenue / forecast.spend
      };

      return set;
    });

    return roi;
  }
};
